import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { haptic } from "../lib/haptics";

/** Pull distance (px, post-resistance) that arms a refresh on release. */
const THRESHOLD = 70;
/** Cap so the indicator never chases a full-screen drag. */
const MAX_PULL = 110;
/** Finger must travel this far down before we treat it as a pull. */
const INTENT = 8;

/**
 * Pull-to-refresh for touch devices. The app locks native overscroll (body
 * overflow hidden + overscroll-behavior none) to keep the browser chrome and
 * tab bar stable, which also removes the platform's own pull-to-refresh — in
 * the installed PWA there is otherwise no way to force-refresh. This restores
 * the gesture on the #root scroll container: pulling past the threshold
 * reloads the app, which refetches data and picks up any waiting
 * service-worker update.
 *
 * The indicator is driven directly through refs (no re-render per frame).
 * The gesture never starts over an open map (html.map-open), a canvas node
 * ([data-node]), or a nested scroller that can still scroll up.
 */
export function PullToRefresh() {
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!window.matchMedia("(pointer: coarse)").matches) return;
    const root = document.getElementById("root");
    if (!root) return;

    let startX = 0;
    let startY = 0;
    let armed = false;
    let pulling = false;
    let pull = 0;

    const setIndicator = (px: number, dragging: boolean) => {
      const el = indicatorRef.current;
      if (!el) return;
      el.style.transition = dragging ? "none" : "transform 0.25s ease, opacity 0.25s ease";
      el.style.opacity = String(Math.min(1, px / THRESHOLD));
      el.style.transform = `translate(-50%, ${px - 48}px)`;
      const icon = iconRef.current;
      if (icon && !refreshingRef.current) {
        icon.style.transform = `rotate(${(px / MAX_PULL) * 270}deg)`;
        icon.style.color = px >= THRESHOLD ? "var(--accent)" : "var(--muted-foreground)";
      }
    };

    // True if something between the touch target and #root has already
    // scrolled down — pulling there should scroll it back up, not refresh.
    const nestedScrolled = (target: EventTarget | null) => {
      let el = target instanceof Element ? target : null;
      while (el && el !== root) {
        if (el.scrollTop > 0) return true;
        el = el.parentElement;
      }
      return false;
    };

    const onStart = (e: TouchEvent) => {
      armed = false;
      if (refreshingRef.current) return;
      if (root.scrollTop > 0) return;
      if (document.documentElement.classList.contains("map-open")) return;
      if (e.target instanceof Element && e.target.closest("[data-node]")) return;
      if (nestedScrolled(e.target)) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      armed = true;
      pulling = false;
    };

    const onMove = (e: TouchEvent) => {
      if (!armed) return;
      const dy = e.touches[0].clientY - startY;
      const dx = e.touches[0].clientX - startX;
      if (!pulling) {
        // Wait for clear downward intent; bail on horizontal swipes,
        // upward scrolls, or once the page has started scrolling.
        if (dy < -4 || Math.abs(dx) > Math.max(INTENT, dy)) {
          armed = false;
          return;
        }
        if (dy < INTENT) return;
        if (root.scrollTop > 0) {
          armed = false;
          return;
        }
        pulling = true;
      }
      e.preventDefault();
      const next = Math.max(0, Math.min(MAX_PULL, (dy - INTENT) * 0.45));
      if (pull < THRESHOLD && next >= THRESHOLD) haptic("light");
      pull = next;
      setIndicator(pull, true);
    };

    const onEnd = () => {
      if (!pulling) {
        armed = false;
        return;
      }
      pulling = false;
      armed = false;
      if (pull >= THRESHOLD && !refreshingRef.current) {
        refreshingRef.current = true;
        setRefreshing(true);
        haptic("medium");
        setIndicator(THRESHOLD, false);
        // Brief pause so the spinner reads before the reload wipes the page.
        setTimeout(() => window.location.reload(), 400);
      } else {
        setIndicator(0, false);
      }
      pull = 0;
    };

    // touchmove must be non-passive: preventDefault stops the scroller's
    // native rubber-banding from fighting the pull.
    root.addEventListener("touchstart", onStart, { passive: true });
    root.addEventListener("touchmove", onMove, { passive: false });
    root.addEventListener("touchend", onEnd);
    root.addEventListener("touchcancel", onEnd);
    return () => {
      root.removeEventListener("touchstart", onStart);
      root.removeEventListener("touchmove", onMove);
      root.removeEventListener("touchend", onEnd);
      root.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  return (
    <div
      ref={indicatorRef}
      className="fixed left-1/2 z-[90] pointer-events-none"
      style={{
        top: "calc(var(--app-nav-height) + 4px)",
        transform: "translate(-50%, -48px)",
        opacity: 0,
      }}
      aria-hidden="true"
    >
      <div className="w-9 h-9 rounded-full bg-card border border-border/40 shadow-lg shadow-foreground/10 flex items-center justify-center">
        <RefreshCw
          ref={iconRef}
          size={16}
          className={refreshing ? "animate-spin text-accent" : "text-muted-foreground"}
        />
      </div>
    </div>
  );
}
